import React, { useState } from 'react';
import { Star, Copy, Edit, Trash2, Calendar, Cpu, BookTemplate as Template, Play, Zap, Wand2, Sparkles, CheckSquare, Square, Clock, FolderOpen, Lock, MoreVertical, Check } from 'lucide-react';
import { Prompt } from '../../types'; // Keep this import
import { TemplateExecutor } from './TemplateExecutor';
import { PromptHistoryModal } from './PromptHistoryModal';
import { ThanosSnapEffect } from '../ui/thanos-snap-effect';
import { useClickOutside } from '../../hooks/useClickOutside';

interface PromptCardProps {
  prompt: Prompt;
  onEdit: (prompt: Prompt) => void;
  onDelete: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onGenerateVariations?: (prompt: Prompt) => void;
  onEnhancePrompt?: (prompt: Prompt) => void;
  onUpdatePrompt?: (id: string, updates: Partial<Prompt>, historyType?: 'enhanced' | 'manual' | 'variation') => void;
  onPromptCopy?: (prompt: Prompt) => void; // New prop for tracking execution
  isBatchMode?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (id: string) => void;
  onDragStart?: (prompt: Prompt) => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
  onMoveToFolder?: (promptId: string) => void;
}

export const PromptCard: React.FC<PromptCardProps> = ({
  prompt,
  onEdit,
  onDelete,
  onToggleFavorite,
  onGenerateVariations,
  onEnhancePrompt,
  onUpdatePrompt,
  onPromptCopy,
  isBatchMode = false,
  isSelected = false,
  onToggleSelection,
  onDragStart,
  onDragEnd,
  isDragging = false,
  onMoveToFolder,
}) => {
  const [showTemplateExecutor, setShowTemplateExecutor] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);

  const actionsMenuRef = useClickOutside(() => {
    setShowActionsMenu(false);
    setMenuPosition(null);
  });

  const handleCopy = async (text?: string) => {
    try {
      await navigator.clipboard.writeText(text || prompt.body);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
      // Track this as an execution
      if (onPromptCopy) {
        onPromptCopy(prompt);
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleTemplateExecutorCopy = (text: string) => {
    // Track template execution
    if (onPromptCopy) {
      onPromptCopy(prompt);
    }
  };

  const handleRestoreVersion = (historyEntry: any) => {
    if (onUpdatePrompt) {
      onUpdatePrompt(prompt.id, { body: historyEntry.body }, 'manual');
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const isInteractiveElement = target.closest('button, a, input, select, textarea');
    
    // Don't trigger any action if clicking on interactive elements
    if (isInteractiveElement) {
      return;
    }
    
    // Handle batch mode selection
    if (isBatchMode && onToggleSelection) {
      e.preventDefault();
      onToggleSelection(prompt.id);
    } else {
      // Handle edit mode for normal interaction
      onEdit(prompt);
    }
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (showActionsMenu) {
      // Close menu if already open
      setShowActionsMenu(false);
      setMenuPosition(null);
      return;
    }
    
    // Calculate absolute position for the dropdown
    const rect = e.currentTarget.getBoundingClientRect();
    const scrollX = window.scrollX || document.documentElement.scrollLeft;
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    
    // Position dropdown below and to the right of the trigger button
    const x = rect.right + scrollX - 160; // 160px = min-width of dropdown
    const y = rect.bottom + scrollY + 4; // 4px gap
    
    setMenuPosition({ x, y });
    setShowActionsMenu(true);
  };
  const handleSelectionToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleSelection) {
      onToggleSelection(prompt.id);
    }
  };

  const handleDragStart = (e: React.DragEvent) => {
    // Don't allow dragging in batch mode
    if (isBatchMode) {
      e.preventDefault();
      return;
    }

    // Set the drag data and effect
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({
      type: 'prompt',
      promptId: prompt.id,
      promptTitle: prompt.title
    }));
    
    // Calculate mouse offset from card top-left for smooth dragging
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    
    if (onDragStart) {
      onDragStart(prompt);
    }
  };
  
  const handleDragEnd = () => {
    if (onDragEnd) {
      onDragEnd();
    }
  };

  const handleSnapDelete = () => {
    onDelete(prompt.id);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return formatDate(date);
  };

  const extractVariablesWithCounts = (text: string): { name: string; count: number }[] => {
    const matches = text.match(/\[([^\]]+)\]/g);
    if (!matches) return [];

    // Count occurrences of each variable
    const variableCounts: Record<string, number> = {};
    matches.forEach(match => {
      const variable = match.slice(1, -1); // Remove brackets
      variableCounts[variable] = (variableCounts[variable] || 0) + 1;
    });

    // Convert to array and sort alphabetically
    return Object.entries(variableCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const variablesWithCounts = extractVariablesWithCounts(prompt.body);

  return (
    <>
      <div 
        draggable={!isBatchMode}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        className={`relative bg-white/98 dark:bg-dark-surface backdrop-blur-sm rounded-lg lg:rounded-xl border 
          border-gray-200/80 dark:border-dark-border p-6 group 
          hover:border-gray-300/90 dark:hover:border-gray-500 
          transform transition-all duration-200 ease-out
          hover:translate-y-[-2px] hover:shadow-[0_8px_24px_rgba(0,0,0,0.12),0_4px_8px_rgba(0,0,0,0.06)]
          dark:hover:shadow-[0_12px_40px_rgba(0,0,0,0.5),0_4px_12px_rgba(0,0,0,0.3)]
          active:opacity-95 active:scale-[0.99] active:translate-y-0
          ${isBatchMode ? 'cursor-pointer' : 'cursor-pointer'} 
          ${isSelected ? 'ring-2 ring-blue-500/60 border-blue-400/80 bg-blue-50/30 dark:bg-blue-900/15' : ''}
          ${isDragging ? 'opacity-50 scale-95 rotate-2' : ''}
          min-h-[240px] lg:min-h-[280px] flex flex-col shadow-md hover:shadow-lg 
          dark:shadow-[0_4px_16px_rgba(0,0,0,0.3),0_1px_4px_rgba(0,0,0,0.2)]
          ring-1 ring-black/5 dark:ring-white/10`}
        onClick={handleCardClick}
        style={{
          cursor: isDragging ? 'grabbing' : 'pointer'
        }}
      >
        
        {/* Floating Top-Right Actions - Absolutely positioned */}
        <div className="absolute top-4 right-4 flex items-center space-x-2 z-10">
          {/* Copy Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
            className={`p-1.5 lg:p-2 rounded-lg transition-all duration-200 ease-out ${
              copied
                ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
            title={copied ? 'Copied!' : 'Copy prompt'}
          >
            {copied ? (
              <Check className="h-4 w-4 lg:h-5 lg:w-5" />
            ) : (
              <Copy className="h-4 w-4 lg:h-5 lg:w-5" />
            )}
          </button>

          {/* Enhance Button */}
          {onEnhancePrompt && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEnhancePrompt(prompt);
              }}
              className="p-1.5 lg:p-2 rounded-lg transition-all duration-200 ease-out text-gray-400 dark:text-gray-500 
                hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20"
              title="Enhance with AI"
            >
              <Sparkles className="h-4 w-4 lg:h-5 lg:w-5" />
            </button>
          )}

          {/* Favorite Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(prompt.id);
            }}
            className={`p-1.5 lg:p-2 rounded-lg transition-all duration-200 ease-out ${
              prompt.isFavorite
                ? 'text-modular-accent-amber hover:text-yellow-600 dark:text-modular-accent-amber dark:hover:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'
                : 'text-gray-400 dark:text-gray-500 hover:text-modular-accent-amber dark:hover:text-modular-accent-amber hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
            }`}
            title={prompt.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Star className={`h-4 w-4 lg:h-5 lg:w-5 ${prompt.isFavorite ? 'fill-current' : ''}`} />
          </button>
        </div>

        {/* Batch Mode Checkbox - Top Left */}
        {isBatchMode && (
          <div className="absolute top-4 left-4 z-10">
            <button
              onClick={handleSelectionToggle}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors flex-shrink-0"
            >
              {isSelected ? (
                <CheckSquare className="h-5 w-5 text-fire-600 dark:text-fire-400" />
              ) : (
                <Square className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              )}
            </button>
          </div>
        )}

        {/* Content Area - Title at the very top */}
        <div className="flex-1 flex flex-col pr-28 sm:pr-32">
          <h3 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-dark-text 
            group-hover:text-gray-800 dark:group-hover:text-gray-100 transition-colors duration-300
            line-clamp-2 leading-tight mb-2 sm:mb-3">
            {prompt.title}
          </h3>
          
          {/* Rich Visual Badges */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {prompt.isFavorite && (
              <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold 
                bg-gradient-to-r from-yellow-100 to-amber-100 dark:from-yellow-900/30 dark:to-amber-900/30 
                text-yellow-700 dark:text-yellow-300 border border-yellow-200/60 dark:border-yellow-700/50 
                shadow-sm transition-all duration-300">
                <Star className="h-3 w-3 mr-1 fill-current" />
                Starred
              </div>
            )}
            
            {prompt.isTemplate && (
              <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold 
                bg-gradient-to-r from-emerald-100 to-green-100 dark:from-emerald-900/30 dark:to-green-900/30 
                text-emerald-700 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-700/50 
                shadow-sm transition-all duration-300">
                <Template className="h-3 w-3 mr-1" />
                Template
              </div>
            )}
            
            {variablesWithCounts.length > 0 && (
              <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold 
                bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 
                text-blue-700 dark:text-blue-300 border border-blue-200/60 dark:border-blue-700/50 
                shadow-sm transition-all duration-300">
                <Zap className="h-3 w-3 mr-1" />
                {variablesWithCounts.length} variable{variablesWithCounts.length !== 1 ? 's' : ''}
              </div>
            )}
            
            {prompt.lastExecutedAt && (
              <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium 
                bg-gradient-to-r from-gray-100 to-slate-100 dark:from-gray-800/40 dark:to-slate-800/40 
                text-gray-600 dark:text-gray-300 border border-gray-200/60 dark:border-gray-700/50 
                shadow-sm transition-all duration-300">
                <Clock className="h-3 w-3 mr-1" />
                {formatRelativeTime(prompt.lastExecutedAt)}
              </div>
            )}
          </div>
        </div>

        {/* Unified Metadata Row */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-2 sm:mb-3">
          <div className="flex items-center">
            <Calendar className="h-3 w-3 mr-1 flex-shrink-0" />
            <span className="whitespace-nowrap">{formatDate(prompt.createdAt)}</span>
          </div>
          {prompt.aiPlatform && (
            <div className="flex items-center">
              <Cpu className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="whitespace-nowrap">{prompt.aiPlatform}</span>
            </div>
          )}
        </div>
        
        {/* Prompt body - with proper line clamping */}
        <div className="flex-1 mb-2 sm:mb-3">
          <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed line-clamp-2 sm:line-clamp-3 lg:line-clamp-4">
            {prompt.body}
          </p>
        </div>

        {/* Variables section */}
        {variablesWithCounts.length > 0 && (
          <div className="mb-2 sm:mb-3">
            <div className="flex flex-wrap gap-1">
              {variablesWithCounts.slice(0, 3).map((variable, index) => (
                <div
                  key={`${variable.name}-${index}`}
                  className="inline-flex items-center justify-between px-1.5 py-1 rounded text-xs font-mono bg-modular-canvas-fog 
                    dark:bg-modular-shadow-quartz/20 text-modular-frosted-indigo dark:text-modular-frosted-indigo 
                    border border-modular-stone-alloy dark:border-modular-graphite-smoke transition-colors duration-300"
                >
                  <div className="flex items-center">
                    <span className="text-modular-frosted-indigo dark:text-modular-frosted-indigo">[</span>
                    <span className="max-w-[50px] sm:max-w-[60px] truncate">{variable.name}</span>
                    <span className="text-modular-frosted-indigo dark:text-modular-frosted-indigo">]</span>
                  </div>
                  {variable.count > 1 && (
                    <span className="ml-1 text-xs bg-modular-champagne-dust dark:bg-modular-graphite-smoke text-modular-frosted-indigo dark:text-modular-ivory-mist px-1 py-0.5 rounded-full font-semibold">
                      x{variable.count}
                    </span>
                  )}
                </div>
              ))}
              {variablesWithCounts.length > 3 && (
                <div className="inline-flex items-center px-1.5 py-1 rounded text-xs 
                  bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                  +{variablesWithCounts.length - 3} more
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action buttons - always visible now */}
        {!isBatchMode && (
          <div className="flex items-center justify-end gap-1 mt-auto">
            {/* Execute - Main action for templates */}
            {prompt.isTemplate && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTemplateExecutor(true);
                }}
                className="flex items-center px-2 py-1.5 text-xs text-emerald-600 dark:text-emerald-400 
                  hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-50 
                  dark:hover:bg-emerald-900/20 rounded-lg transition-all duration-200 ease-out 
                  border border-emerald-200 dark:border-emerald-800 hover:border-emerald-300
                  dark:hover:border-emerald-700 flex-shrink-0 font-medium min-w-0"
                title="Execute template"
              >
                <Play className="h-3 w-3 mr-1" />
                <span className="hidden sm:inline">Execute</span>
                <span className="sm:hidden">Ex</span>
              </button>
            )}
            
            {/* Vary - Compact button for variations */}
            {onGenerateVariations && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onGenerateVariations(prompt);
                }}
                className="flex items-center px-2 py-1.5 text-xs text-purple-600 dark:text-purple-400 
                  hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-50 
                  dark:hover:bg-purple-900/20 rounded-lg transition-all duration-200 ease-out 
                  border border-purple-200 dark:border-purple-800 hover:border-purple-300
                  dark:hover:border-purple-700 flex-shrink-0 min-w-0"
                title="Generate variations"
              >
                <Wand2 className="h-3 w-3 mr-1" />
                <span className="hidden sm:inline">Vary</span>
                <span className="sm:hidden">V</span>
              </button>
            )}
            
            {/* Delete - Trash icon */}
            <ThanosSnapEffect onSnapComplete={handleSnapDelete}>
              <button
                className="p-1.5 text-red-500 dark:text-red-400 hover:text-red-600 
                  dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 
                  rounded-lg transition-all duration-200 ease-out flex-shrink-0"
                title="Delete prompt"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </ThanosSnapEffect>
            
            {/* More - Three dots */}
            <div className="relative flex-shrink-0">
              <button
                onClick={handleMenuClick}
                className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 
                  dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 
                  rounded-lg transition-all duration-200 ease-out"
                title="More actions"
              >
                <MoreVertical className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Portal-like Dropdown Menu - Rendered at top level */}
      {showActionsMenu && menuPosition && (
        <div
          ref={actionsMenuRef}
          className="fixed bg-white/98 dark:bg-dark-surface/98 backdrop-blur-md rounded-xl 
            shadow-2xl dark:shadow-dark-elevated border border-gray-200/80 dark:border-dark-border/80 
            py-3 z-[9999] min-w-[180px] transition-all duration-200 ease-out"
          style={{
            left: `${menuPosition.x}px`,
            top: `${menuPosition.y}px`,
          }}
        >
          {onGenerateVariations && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onGenerateVariations(prompt);
                setShowActionsMenu(false);
                setMenuPosition(null);
              }}
              className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-300 
                hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:text-purple-600 
                dark:hover:text-purple-400 transition-all duration-200 flex items-center font-medium"
            >
              <Wand2 className="h-4 w-4 mr-3 text-purple-500 dark:text-purple-400" />
              Generate Variations
            </button>
          )}
          
          {onMoveToFolder && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMoveToFolder(prompt.id);
                setShowActionsMenu(false);
                setMenuPosition(null);
              }}
              className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-300 
                hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 
                dark:hover:text-indigo-400 transition-all duration-200 flex items-center font-medium"
            >
              <FolderOpen className="h-4 w-4 mr-3 text-indigo-500 dark:text-indigo-400" />
              Move to Folder
            </button>
          )}
        </div>
      )}
      {showTemplateExecutor && (
        <TemplateExecutor
          template={prompt}
          isOpen={showTemplateExecutor}
          onClose={() => setShowTemplateExecutor(false)}
          onCopy={handleTemplateExecutorCopy}
          onUpdateTemplate={onUpdatePrompt}
        />
      )}

      {showHistoryModal && (
        <PromptHistoryModal
          isOpen={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          prompt={prompt}
          onRestoreVersion={handleRestoreVersion}
        />
      )}
    </>
  );
};